import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { NewspaperClipping, ArrowSquareOut, ArrowsClockwise, MagnifyingGlass, FunnelSimple, Rss, WarningCircle } from '@phosphor-icons/react';
import { formatLabel, BADGE, timeAgo } from '@/lib/format';
import ResolutionModal from '@/components/ResolutionModal';
import BulkActionBar from '@/components/BulkActionBar';

export default function MediaAlertsPage() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [checking, setChecking] = useState(false);
  const [resModal, setResModal] = useState({ open: false, id: null, resolution: null, matchName: null });
  const [mainTab, setMainTab] = useState('feeds'); // 'feeds' | 'pending'
  // Filters (apply to pending-review tab)
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCase, setFilterCase] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const navigate = useNavigate();
  useEffect(() => { document.title = "Radar — Media Alerts"; }, []);

  const fetchData = async () => {
    try {
      const [s, c] = await Promise.all([api.get('/media-alerts/stats'), api.get('/media-alerts/config')]);
      setStats(s.data);
      setConfigs(c.data.configs || []);
      const allAlerts = [];
      for (const cfg of (c.data.configs || [])) {
        try {
          const r = await api.get(`/media-alerts/case/${cfg.case_id}`);
          allAlerts.push(...(r.data.alerts || []).map(a => ({ ...a, case_name: cfg.case_name })));
        } catch {}
      }
      setAlerts(allAlerts);
    } catch {}
  };

  useEffect(() => { fetchData(); }, []);

  const handleCheck = async () => {
    setChecking(true);
    try { await api.post('/media-alerts/check-now'); setTimeout(fetchData, 3000); } catch {}
    setChecking(false);
  };

  const handleResolve = (id, status, name) => setResModal({ open: true, id, resolution: status, matchName: name });
  const confirmResolve = async (comment) => {
    try {
      await api.put(`/media-alerts/${resModal.id}/resolve`, { status: resModal.resolution, comment });
      setResModal({ open: false, id: null, resolution: null, matchName: null });
      fetchData();
    } catch {}
  };

  // ─── Multi-select bulk actions ─────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const runBulkMediaAction = async (status, comment) => {
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map(id => api.put(`/media-alerts/${id}/resolve`, { status, comment: comment || `Bulk ${status}` }))
    );
    let ok = 0, failed = 0;
    const failedIds = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok += 1;
      else { failed += 1; failedIds.push(ids[i]); }
    });
    if (failed > 0) {
      console.warn('[MediaAlerts bulk action] failures:', failedIds, results.filter(r => r.status === 'rejected'));
    }
    await fetchData();
    setSelectedIds(new Set(failedIds));
    return { ok, failed };
  };

  const uniqueSources = useMemo(() => Array.from(new Set(alerts.map(a => a.source).filter(Boolean))).sort(), [alerts]);
  const uniqueCases = useMemo(() => Array.from(new Set(alerts.map(a => a.case_name).filter(Boolean))).sort(), [alerts]);

  const filtered = useMemo(() => {
    const fromTs = filterFrom ? new Date(filterFrom).getTime() : null;
    const toTs = filterTo ? new Date(filterTo).getTime() + 86_399_000 : null;
    return alerts.filter(a => {
      if (filterStatus && a.status !== filterStatus) return false;
      if (filterCase && a.case_name !== filterCase) return false;
      if (filterSource && a.source !== filterSource) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const hay = `${a.title || ''} ${a.summary || ''} ${a.case_name || ''} ${a.source || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (fromTs || toTs) {
        const d = a.published_date ? Date.parse(a.published_date) : (a.ingested_at ? Date.parse(a.ingested_at) : NaN);
        if (!Number.isNaN(d)) {
          if (fromTs && d < fromTs) return false;
          if (toTs && d > toTs) return false;
        }
      }
      return true;
    });
  }, [alerts, filterSearch, filterStatus, filterCase, filterSource, filterFrom, filterTo]);

  const resetFilters = () => { setFilterSearch(''); setFilterStatus(''); setFilterCase(''); setFilterSource(''); setFilterFrom(''); setFilterTo(''); };
  const hasActiveFilter = !!(filterSearch || filterStatus || filterCase || filterSource || filterFrom || filterTo);

  const pending = filtered.filter(a => a.status === 'pending');
  const flagged = filtered.filter(a => a.status === 'flagged');
  const dismissed = filtered.filter(a => a.status === 'dismissed');

  // Feed-level aggregates for "Active Feeds" tab
  const pendingByCase = useMemo(() => {
    const m = {};
    for (const a of alerts) {
      if (a.status === 'pending' && a.case_id) m[a.case_id] = (m[a.case_id] || 0) + 1;
    }
    return m;
  }, [alerts]);

  const activeFeedRows = useMemo(() => {
    return (configs || []).map(c => ({
      ...c,
      pending_count: pendingByCase[c.case_id] || 0,
    }));
  }, [configs, pendingByCase]);

  const totalPending = alerts.filter(a => a.status === 'pending').length;
  const totalFlagged = alerts.filter(a => a.status === 'flagged').length;

  const goToCaseMedia = (caseId) => navigate(`/cases/${caseId}?tab=media`);

  return (
    <div data-testid="media-alerts-page">
      <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-1">Media Alerts</h1>
      <p className="text-sm text-[#52525B] mb-6">Google Alerts media monitoring across all cases</p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Active Feeds</p><p className="text-2xl font-bold text-[#0052FF]">{configs.filter(c => c.enabled).length}</p></div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Pending Articles</p><p className="text-2xl font-bold text-red-600">{totalPending}</p></div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Flagged</p><p className="text-2xl font-bold text-amber-600">{totalFlagged}</p></div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-4"><p className="text-xs text-[#52525B]">Total Articles</p><p className="text-2xl font-bold">{stats?.total_alerts || 0}</p></div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <button onClick={handleCheck} disabled={checking} data-testid="check-feeds-btn" className="flex items-center gap-2 px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">
          <ArrowsClockwise size={16} className={checking ? 'animate-spin' : ''} /> {checking ? 'Checking...' : 'Check All Feeds Now'}
        </button>
        <span className="text-xs text-[#A1A1AA]">Last checked: {configs.some(c => c.last_checked) ? timeAgo(configs.reduce((m, c) => c.last_checked && (!m || c.last_checked > m) ? c.last_checked : m, null)) : 'N/A'}</span>
      </div>

      {/* MAIN TABS */}
      <div className="flex gap-1 mb-4 border-b border-[#E4E4E7]">
        <button onClick={() => setMainTab('feeds')} data-testid="tab-active-feeds" className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${mainTab === 'feeds' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>
          <Rss size={14} /> Active Feeds <span className={`px-1.5 py-0.5 text-[10px] rounded ${mainTab === 'feeds' ? 'bg-[#0052FF]/10 text-[#0052FF]' : 'bg-[#F0F0F1] text-[#52525B]'}`}>{configs.length}</span>
        </button>
        <button onClick={() => setMainTab('pending')} data-testid="tab-pending-review" className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${mainTab === 'pending' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>
          <WarningCircle size={14} /> Pending Review <span className={`px-1.5 py-0.5 text-[10px] rounded ${totalPending > 0 ? 'bg-red-100 text-red-700' : 'bg-[#F0F0F1] text-[#52525B]'}`}>{totalPending}</span>
        </button>
      </div>

      {/* ACTIVE FEEDS TAB */}
      {mainTab === 'feeds' && (
        <div data-testid="active-feeds-panel">
          {activeFeedRows.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center">
              <NewspaperClipping size={40} className="mx-auto text-[#A1A1AA] mb-3" />
              <p className="text-sm text-[#52525B]">No media alerts configured</p>
              <p className="text-xs text-[#A1A1AA] mt-1">Set up Google Alerts on individual cases to monitor for media mentions</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                    <th className="text-left px-4 py-2 text-xs font-medium text-[#52525B]">Case</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[#52525B]">Status</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[#52525B]">Articles</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[#52525B]">Pending</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[#52525B]">Last Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {activeFeedRows.map(c => (
                    <tr key={c.id} data-testid={`feed-row-${c.case_id}`} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8] cursor-pointer transition-colors" onClick={() => goToCaseMedia(c.case_id)}>
                      <td className="px-4 py-2.5 text-sm font-medium text-[#0052FF] hover:underline">{c.case_name}</td>
                      <td className="px-4 py-2.5"><span className={`${BADGE} ${c.enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{c.enabled ? 'Active' : 'Disabled'}</span></td>
                      <td className="px-4 py-2.5 text-sm">{c.articles_ingested || 0}</td>
                      <td className="px-4 py-2.5 text-sm">
                        {c.pending_count > 0 ? (
                          <span className={`${BADGE} bg-red-50 text-red-700 border-red-200`}>{c.pending_count} pending</span>
                        ) : (
                          <span className="text-xs text-[#A1A1AA]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#A1A1AA]">{c.last_checked ? timeAgo(c.last_checked) : 'Never'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* PENDING REVIEW TAB */}
      {mainTab === 'pending' && (
        <div data-testid="pending-review-panel">
          {/* Filters */}
          <div className="bg-white border border-[#E4E4E7] rounded-md p-3 mb-4" data-testid="media-filters">
            <div className="flex items-center gap-2 mb-2">
              <FunnelSimple size={14} className="text-[#52525B]" />
              <span className="text-xs font-medium uppercase tracking-wider text-[#52525B]">Filters</span>
              {hasActiveFilter && (
                <button onClick={resetFilters} data-testid="clear-media-filters" className="ml-auto text-xs text-[#0052FF] hover:underline">Clear all</button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <div className="relative md:col-span-2">
                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Search title, summary, case…" className="w-full pl-7 pr-2 py-1.5 text-xs border border-[#E4E4E7] rounded-md" data-testid="media-filter-search" />
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E4E4E7] rounded-md" data-testid="media-filter-status">
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="flagged">Flagged</option>
                <option value="dismissed">Dismissed</option>
              </select>
              <select value={filterCase} onChange={e => setFilterCase(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E4E4E7] rounded-md" data-testid="media-filter-case">
                <option value="">All Cases</option>
                {uniqueCases.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E4E4E7] rounded-md" data-testid="media-filter-source">
                <option value="">All Sources</option>
                {uniqueSources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-1 md:col-span-1">
                <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="px-1.5 py-1.5 text-xs border border-[#E4E4E7] rounded-md" title="From date" data-testid="media-filter-from" />
                <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="px-1.5 py-1.5 text-xs border border-[#E4E4E7] rounded-md" title="To date" data-testid="media-filter-to" />
              </div>
            </div>
            {hasActiveFilter && (
              <p className="text-xs text-[#A1A1AA] mt-2">Showing {filtered.length} of {alerts.length} articles</p>
            )}
          </div>

          {/* Pending */}
          {pending.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2 py-1 border-b-2 border-red-300">
                <h3 className="text-xs font-bold uppercase tracking-wider text-red-700">Pending Review ({pending.length})</h3>
                <label className="flex items-center gap-2 text-xs text-[#52525B] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pending.length > 0 && pending.every(a => selectedIds.has(a.id))}
                    onChange={() => {
                      const allSel = pending.every(a => selectedIds.has(a.id));
                      setSelectedIds(allSel ? new Set() : new Set(pending.map(a => a.id)));
                    }}
                    className="w-4 h-4 cursor-pointer accent-[#0052FF]"
                    data-testid="media-select-all"
                    aria-label="Select all pending articles"
                  />
                  Select all
                </label>
              </div>
              <div className="space-y-2">
                {pending.map(a => {
                  const checked = selectedIds.has(a.id);
                  return (
                  <div key={a.id} className={`bg-white border rounded-md p-4 ${checked ? 'ring-2 ring-[#0052FF]/40 border-[#0052FF]/40' : 'border-[#E4E4E7]'}`} data-testid={`pending-alert-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(a.id)}
                          className="mt-1 w-4 h-4 cursor-pointer accent-[#0052FF]"
                          data-testid={`media-checkbox-${a.id}`}
                          aria-label={`Select article ${a.title}`}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">📰 {a.title || 'Untitled Article'}</p>
                          <div className="flex gap-3 mt-1 text-xs text-[#52525B] flex-wrap">
                            <span>Case: <button onClick={() => goToCaseMedia(a.case_id)} className="text-[#0052FF] hover:underline">{a.case_name || 'Unknown'}</button></span>
                            {a.source && <span>Source: {a.source}</span>}
                            {a.published_date && <span>{a.published_date}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] flex items-center gap-1"><ArrowSquareOut size={12} /> Read</a>}
                        <button onClick={() => handleResolve(a.id, 'dismissed', a.title)} className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200">Dismiss</button>
                        <button onClick={() => handleResolve(a.id, 'flagged', a.title)} className="px-2.5 py-1 text-xs bg-amber-50 text-amber-700 rounded-md hover:bg-amber-100">Flag</button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Flagged */}
          {flagged.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2 py-1 border-b-2 border-amber-300">Flagged for Review ({flagged.length})</h3>
              <div className="space-y-2">
                {flagged.map(a => (
                  <div key={a.id} className="bg-amber-50/30 border border-amber-200 rounded-md p-3 flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">📰 {a.title}</p>
                      <div className="flex gap-3 mt-1 text-xs text-[#52525B]">
                        <span>Case: <button onClick={() => goToCaseMedia(a.case_id)} className="text-[#0052FF] hover:underline">{a.case_name}</button></span>
                        {a.source && <span>Source: {a.source}</span>}
                        {a.published_date && <span>{a.published_date}</span>}
                      </div>
                    </div>
                    {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 text-xs border border-amber-200 bg-white rounded-md hover:bg-amber-50 flex items-center gap-1 shrink-0"><ArrowSquareOut size={12} /> Read</a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dismissed (collapsed) */}
          {dismissed.length > 0 && (
            <details>
              <summary className="text-xs text-[#A1A1AA] cursor-pointer py-2">▶ {dismissed.length} recently dismissed articles</summary>
              <div className="mt-2 bg-white border border-[#E4E4E7] rounded-md divide-y divide-[#F0F0F1]">
                {dismissed.slice(0, 30).map(a => (
                  <div key={a.id} className="px-4 py-2 text-xs flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{a.title}</p>
                      <div className="flex gap-2 text-[#A1A1AA] mt-0.5">
                        <button onClick={() => goToCaseMedia(a.case_id)} className="text-[#0052FF] hover:underline">{a.case_name}</button>
                        {a.source && <span>· {a.source}</span>}
                        {a.resolved_by && <span>· dismissed by {a.resolved_by}</span>}
                      </div>
                    </div>
                    {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 text-xs border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] flex items-center gap-1 shrink-0"><ArrowSquareOut size={11} /> Read</a>}
                  </div>
                ))}
              </div>
            </details>
          )}

          {pending.length === 0 && flagged.length === 0 && dismissed.length === 0 && (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center">
              <NewspaperClipping size={40} className="mx-auto text-[#A1A1AA] mb-3" />
              <p className="text-sm text-[#52525B]">No articles to review</p>
              <p className="text-xs text-[#A1A1AA] mt-1">Pending articles from all feeds will appear here</p>
            </div>
          )}
        </div>
      )}

      <ResolutionModal isOpen={resModal.open} resolution={resModal.resolution} matchName={resModal.matchName} onConfirm={confirmResolve} onCancel={() => setResModal({ open: false, id: null, resolution: null, matchName: null })} />

      {/* Bulk Action Bar — pending Media articles */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onAction={(actionKey, comment) => runBulkMediaAction(actionKey, comment)}
        onClear={clearSelection}
        actions={[
          { key: 'flagged',   label: 'Flag',    className: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
          { key: 'dismissed', label: 'Dismiss', className: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
        ]}
      />
    </div>
  );
}
